import React, { useEffect, useMemo, useState } from "react";
import Topbar from "@/components/Topbar";
import { useAuth } from "@/contexts/AuthContext";
import { TeamPerformance } from "@/components/TeamPerformance";
import { AgentDetailSheet } from "@/components/AgentDetailSheet";
import { fmtDuration, REASON_OPTIONS, STATUS_OPTIONS, InfoTip, ContactReasons, KpiRules } from "@/components/DashboardParts";
import { api, API_BASE } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ChatCircleDots, UsersThree, TrendUp, Package, CurrencyDollar,
  ArrowUpRight, ArrowDownRight, Star, Robot, Users, Clock,
  FileXls, FilePdf, FileCsv, Sparkle, ChartLineUp, Gauge, Hourglass,
} from "@phosphor-icons/react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip,
  LineChart, Line, PieChart, Pie, Cell, CartesianGrid, Legend,
} from "recharts";
import { toast } from "sonner";

const COLORS = ["#0052CC", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#06B6D4"];

function _todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function _isoDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const BU_LABELS = {
  sin_clasificar: "Sin clasificar", info: "Información general", ventas: "Ventas", servicio: "Servicio",
  repuestos: "Repuestos", financiamiento: "Financiamiento",
};


const CHANNELS = [
  { value: "", labelEs: "Todos los canales", labelEn: "All channels" },
  { value: "whatsapp", labelEs: "WhatsApp", labelEn: "WhatsApp" },
  { value: "instagram", labelEs: "Instagram", labelEn: "Instagram" },
  { value: "messenger", labelEs: "Messenger", labelEn: "Messenger" },
];

function Kpi({ icon: Icon, label, value, delta, tone = "default", testid, hint, placeholder }) {
  const toneClass = tone === "danger" ? "text-red-500" : tone === "warn" ? "text-amber-500" : tone === "good" ? "text-emerald-500" : "text-foreground";
  return (
    <div className="rounded-lg p-4 bg-card border border-border hover:-translate-y-0.5 hover:shadow-sm transition-all min-w-0" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="w-8 h-8 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <Icon size={15} weight="duotone" />
        </div>
        <div className="flex items-center gap-1.5">
        {placeholder ? (
          <Badge variant="outline" className="text-[9px] uppercase border-amber-500/40 text-amber-600">
            Próximamente
          </Badge>
        ) : delta !== undefined && delta !== null ? (
          <div className={`text-[10px] font-mono flex items-center gap-0.5 px-1.5 py-0.5 rounded ${delta >= 0 ? "bg-emerald-500/10 text-emerald-600" : "bg-red-500/10 text-red-600"}`}>
            {delta >= 0 ? <ArrowUpRight size={10}/> : <ArrowDownRight size={10}/>}
            {Math.abs(delta)}%
          </div>
        ) : null}
        <InfoTip id={testid} />
        </div>
      </div>
      <div className={`mt-4 font-heading text-2xl font-bold tracking-[-0.02em] leading-none ${toneClass}`}>
        {placeholder ? "—" : (value ?? "—")}
      </div>
      <div className="mt-1 text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold line-clamp-2">
        {label}
      </div>
      {hint && !placeholder && (
        <div className="mt-0.5 text-[10px] text-muted-foreground truncate">{hint}</div>
      )}
    </div>
  );
}

function SectionTitle({ number, title, subtitle }) {
  return (
    <div className="mb-3 mt-8 first:mt-0 flex items-baseline gap-3">
      <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0">
        {number}
      </div>
      <div>
        <div className="font-heading font-bold text-lg leading-tight">{title}</div>
        {subtitle && <div className="text-xs text-muted-foreground">{subtitle}</div>}
      </div>
    </div>
  );
}

function ChartCard({ title, children, className = "" }) {
  return (
    <div className={`rounded-lg border border-border bg-card p-4 ${className}`}>
      <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold mb-3">{title}</div>
      {children}
    </div>
  );
}

async function downloadFile(url, filename) {
  try {
    const token = localStorage.getItem("sdm_token") || sessionStorage.getItem("sdm_token");
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(a.href);
  } catch (e) {
    toast.error(`Export failed: ${e.message}`);
  }
}

export default function Dashboard() {
  const { lang } = useLang();
  const { user } = useAuth();
  const [agentDetail, setAgentDetail] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(_isoDaysAgo(29));
  const [dateTo, setDateTo] = useState(_todayIso());
  const [channel, setChannel] = useState("");
  const [reason, setReason] = useState("");
  const [agentId, setAgentId] = useState("");
  const [status, setStatus] = useState("");
  const [agentOptions, setAgentOptions] = useState([]);
  const [exportBusy, setExportBusy] = useState(null);

  useEffect(() => {
    if (!dateFrom || !dateTo) return;
    const params = new URLSearchParams();
    params.set("from", dateFrom);
    params.set("to", dateTo);
    if (channel) params.set("channel", channel);
    if (reason) params.set("reason", reason);
    if (agentId) params.set("agent_id", agentId);
    if (status) params.set("status", status);
    setLoading(true);
    api.get(`/analytics/dashboard?${params.toString()}`)
      .then((r) => {
        setData(r.data);
        setAgentOptions((prev) => {
          const m = new Map(prev.map((a) => [a.id, a]));
          (r.data?.team?.rows || []).forEach((a) => m.set(a.agent_id, { id: a.agent_id, name: a.name }));
          return [...m.values()].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        });
      })
      .catch(() => toast.error(lang === "es" ? "No se pudo cargar el panel" : "Could not load the dashboard"))
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo, channel, reason, agentId, status]);

  const op = data?.operacion || {};
  const cx = data?.customer_experience || {};
  const team = data?.team?.rows || [];

  const filterQs = useMemo(() => {
    const p = new URLSearchParams();
    p.set("from", dateFrom);
    p.set("to", dateTo);
    if (channel) p.set("channel", channel);
    if (reason) p.set("reason", reason);
    if (agentId) p.set("agent_id", agentId);
    if (status) p.set("status", status);
    return p.toString();
  }, [dateFrom, dateTo, channel, reason, agentId, status]);
  const ab = data?.abandonment || {};

  async function exportAs(fmt) {
    setExportBusy(fmt);
    try {
      const url = `${API_BASE}/analytics/export/${fmt}?${filterQs}`;
      const ext = fmt === "xlsx" ? "xlsx" : fmt;
      await downloadFile(url, `sdm-dashboard-${dateFrom}_${dateTo}.${ext}`);
      toast.success(lang === "es" ? `Exportado ${fmt.toUpperCase()}` : `Exported ${fmt.toUpperCase()}`);
    } finally {
      setExportBusy(null);
    }
  }

  const t = (es, en) => (lang === "es" ? es : en);

  return (
    <>
      <Topbar
        title={t("Panel ejecutivo", "Executive dashboard")}
        subtitle={t("KPIs consolidados por unidad de negocio y equipo", "Consolidated KPIs by business unit and team")}
      />
      <div className="p-6 space-y-4 animate-fade-in flex-1 overflow-y-auto min-h-0" data-testid="dashboard-root">
        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card p-3">
          <div
            className="flex flex-wrap items-center gap-1.5 text-xs min-w-0"
            data-testid="filter-custom-dates"
          >
            <span className="text-muted-foreground uppercase text-[10px] font-medium">{t("Desde", "From")}</span>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              max={dateTo || _todayIso()}
              className="h-8 rounded-md border border-input bg-background px-2 text-xs"
              data-testid="filter-date-from"
            />
            <span className="text-muted-foreground uppercase text-[10px] font-medium">{t("hasta", "to")}</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              min={dateFrom}
              max={_todayIso()}
              className="h-8 rounded-md border border-input bg-background px-2 text-xs"
              data-testid="filter-date-to"
            />
            <div className="flex items-center gap-1 ml-1">
              <button
                type="button"
                onClick={() => { setDateFrom(_isoDaysAgo(6)); setDateTo(_todayIso()); }}
                className="text-[10px] px-1.5 py-0.5 rounded border border-border hover:bg-muted"
                data-testid="filter-quick-7d"
              >
                {t("Últ. 7d", "Last 7d")}
              </button>
              <button
                type="button"
                onClick={() => { setDateFrom(_isoDaysAgo(29)); setDateTo(_todayIso()); }}
                className="text-[10px] px-1.5 py-0.5 rounded border border-border hover:bg-muted"
                data-testid="filter-quick-30d"
              >
                {t("Últ. 30d", "Last 30d")}
              </button>
              <button
                type="button"
                onClick={() => {
                  const now = new Date();
                  setDateFrom(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`);
                  setDateTo(_todayIso());
                }}
                className="text-[10px] px-1.5 py-0.5 rounded border border-border hover:bg-muted"
                data-testid="filter-quick-mtd"
              >
                {t("Este mes", "This month")}
              </button>
            </div>
          </div>
          <select
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            data-testid="filter-channel"
            className="text-xs h-7 rounded-md border border-input bg-background px-2"
          >
            {CHANNELS.map((c) => (
              <option key={c.value} value={c.value}>{t(c.labelEs, c.labelEn)}</option>
            ))}
          </select>
          {[["filter-reason", reason, setReason, REASON_OPTIONS],
            ["filter-agent", agentId, setAgentId, [["", "Todos los agentes"], ...agentOptions.map((o) => [o.id, o.name])]],
            ["filter-status", status, setStatus, STATUS_OPTIONS]].map(([tid, val, set, opts]) => (
            <select key={tid} value={val} onChange={(e) => set(e.target.value)} data-testid={tid}
              className="text-xs h-7 rounded-md border border-input bg-background px-2 max-w-[220px]">
              {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          ))}
          <div className="ml-auto flex flex-wrap items-center gap-1.5">
            <span className="text-[10px] text-muted-foreground mr-1">{t("Exportar", "Export")}</span>
            <Button size="sm" variant="outline" onClick={() => exportAs("csv")} disabled={exportBusy !== null} data-testid="export-csv">
              <FileCsv size={12} className="mr-1" /> CSV
            </Button>
            <Button size="sm" variant="outline" onClick={() => exportAs("xlsx")} disabled={exportBusy !== null} data-testid="export-xlsx">
              <FileXls size={12} className="mr-1" /> Excel
            </Button>
            <Button size="sm" variant="outline" onClick={() => exportAs("pdf")} disabled={exportBusy !== null} data-testid="export-pdf">
              <FilePdf size={12} className="mr-1" /> PDF
            </Button>
          </div>
        </div>

        {loading && !data ? (
          <div className="p-12 text-center text-sm text-muted-foreground">Cargando…</div>
        ) : (
          <>

        {/* 1. OPERACIÓN GENERAL */}
        <SectionTitle number={1} title={t("Operación General", "General Operations")}
          subtitle={t("Volumen y estado de conversaciones", "Conversation volume and status")} />
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-8 gap-3">
          <Kpi icon={ChatCircleDots} label={t("Total conversaciones", "Total conversations")}
            value={op.total} delta={op.trend_delta_pct} testid="kpi-total-conv" />
          <Kpi icon={ChartLineUp} label={t("Atendidas", "Attended")} value={op.attended} testid="kpi-attended" />
          <Kpi icon={Hourglass} label={t("Abandonadas", "Abandoned")} value={ab.total} hint={`${ab.total_pct ?? 0}% del total`} tone="warn" testid="kpi-abandoned" />
          <Kpi icon={Robot} label={t("Abandono en bot", "Abandoned with bot")} value={ab.bot} hint={`${ab.bot_pct ?? 0}% del total`} tone="warn" testid="kpi-abandoned-bot" />
          <Kpi icon={Users} label={t("Abandono en agente", "Abandoned with agent")} value={ab.agent} hint={`${ab.agent_pct ?? 0}% del total`} tone="warn" testid="kpi-abandoned-agent" />
          <Kpi icon={Sparkle} label={t("Cerradas", "Closed")} value={op.closed} tone="good" testid="kpi-closed" />
          <Kpi icon={UsersThree} label={t("Escaladas", "Escalated")} value={op.escalated} tone="danger" testid="kpi-escalated" />
          <Kpi icon={ChatCircleDots} label={t("Abiertas", "Open")} value={op.open} testid="kpi-open" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-3">
          <ChartCard title={t("Volumen últimos 30 días", "Volume last 30 days")} className="lg:col-span-2">
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={op.volume_30d || []}>
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} />
                <XAxis dataKey="date" tick={{ fontSize: 9 }} interval={4} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke={COLORS[0]} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard title={t("Por canal", "By channel")}>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={op.by_channel || []} dataKey="count" nameKey="channel" innerRadius={40} outerRadius={70}>
                  {(op.by_channel || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 10 }} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
        <div className="mt-3">
          <ChartCard title={t("Por unidad de negocio", "By business unit")}>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={(op.by_business_unit || []).map((b) => ({ ...b, unit: BU_LABELS[b.unit] || b.unit }))}>
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} />
                <XAxis dataKey="unit" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="count" fill={COLORS[2]} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        <SectionTitle number={2} title={t("Atención al Cliente", "Customer Experience")}
          subtitle={t("Tiempos, resolución y satisfacción", "Times, resolution and satisfaction")} />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="kpi-cx">
          <Kpi icon={Robot} label={t("Respuesta del bot", "Bot reply")} value={fmtDuration(cx.avg_bot_response_seconds)} testid="kpi-bot-time" />
          <Kpi icon={Users} label={t("Primera respuesta del agente", "Agent first reply")} value={fmtDuration(cx.avg_human_frt_seconds)}
            hint={cx.human_frt_count ? `${cx.human_frt_count} traspasos` : t("Sin traspasos", "No handoffs")} testid="kpi-human-frt" />
          <Kpi icon={Robot} label={t("AHT bot", "Bot AHT")} value={fmtDuration(cx.bot_aht_seconds)}
            hint={cx.bot_aht_count ? t(`${cx.bot_aht_count} conversaciones solo bot`, `${cx.bot_aht_count} bot-only conversations`) : t("Sin conversaciones solo bot", "No bot-only conversations")} testid="kpi-bot-aht" />
          <Kpi icon={Users} label={t("AHT humano", "Human AHT")} value={fmtDuration(cx.human_aht_seconds)}
            hint={cx.human_aht_count ? t(`${cx.human_aht_count} casos atendidos por agentes`, `${cx.human_aht_count} agent cases`) : t("Sin casos de agentes", "No agent cases")} testid="kpi-aht" />
          <Kpi icon={Hourglass} label={t("Tiempo total de atención", "Total attention time")} value={fmtDuration(cx.total_attention_seconds)}
            hint={cx.total_attention_count ? `${cx.total_attention_count} conversaciones resueltas` : t("Sin cierres en el rango", "No closures in range")} testid="kpi-resolution" />
          <Kpi icon={Robot} label={t("% Resolución IA", "% AI resolution")} value={`${cx.ia_resolution_pct ?? 0}%`}
            tone={cx.ia_resolution_pct > 60 ? "good" : "warn"} testid="kpi-ia-resolution" />
          <Kpi icon={UsersThree} label={t("% Resolución humana", "% Human resolution")} value={`${cx.human_resolution_pct ?? 0}%`} testid="kpi-human-resolution" />
          <Kpi icon={Star} label={t("CSAT", "CSAT")}
            value={cx.csat_avg != null ? `${cx.csat_avg} ★` : null}
            hint={cx.csat_count ? `${cx.csat_count} ${t("respuestas", "responses")}` : t("Sin encuestas en el rango", "No surveys in range")} testid="kpi-csat" />
        </div>

        <SectionTitle number={3} title={t("Principales razones de contacto", "Top contact reasons")}
          subtitle={t("Volumen, % y completados por motivo · cada conversación cuenta una sola vez", "Volume, % and completed per reason · each conversation counted once")} />
        <ContactReasons rows={data?.contact_reasons || []} />
        {/* 5. RENDIMIENTO DEL EQUIPO */}
        <SectionTitle number={4} title={t("Rendimiento del Equipo", "Team Performance")}
          subtitle={t("Desempeño por asesor en el rango seleccionado · clic en una columna para ordenar o en un asesor para ver su detalle", "Per-advisor performance in the selected range · click a column to sort or an advisor for details")} />
        <TeamPerformance rows={team} t={t} onSelect={setAgentDetail} />
        <AgentDetailSheet agentId={agentDetail} dateFrom={dateFrom} dateTo={dateTo} onClose={() => setAgentDetail(null)} />
        <KpiRules />

        <div className="text-[10px] text-muted-foreground text-center py-4">
          {t(
            `Generado: ${data?.generated_at || "—"} · Rango: ${dateFrom} → ${dateTo}`,
            `Generated: ${data?.generated_at || "—"} · Range: ${dateFrom} → ${dateTo}`
          )}
        </div>
          </>
        )}
      </div>
    </>
  );
}
